import { alpha, styled } from '@mui/material/styles';
import Paper from '@mui/material/Paper';

const CalendarSurface = styled(Paper)(({ theme }) => {
    const weekendBackground = alpha(theme.palette.grey[100], 0.5);

    return {
        overflow: 'hidden',
        borderRadius: theme.shape.borderRadius * 2,
        fontFamily: theme.typography.fontFamily,

        '& .fc': {
            '--fc-border-color': theme.palette.divider,
            '--fc-page-bg-color': theme.palette.background.paper,
            '--fc-neutral-bg-color': theme.palette.grey[50],
            fontSize: theme.typography.body2.fontSize
        },

        '& .fc .fc-daygrid-body': {
            transition: theme.transitions.create('opacity', {
                duration: theme.transitions.duration.shorter
            })
        },

        '&[aria-busy="true"] .fc .fc-daygrid-body': {
            opacity: 0.6,
            transitionDelay: '200ms'
        },

        '& .fc .fc-scrollgrid': {
            border: 'none'
        },

        '& .fc a': {
            color: 'inherit',
            textDecoration: 'none'
        },

        '& .fc .fc-col-header-cell': {
            backgroundColor: theme.palette.grey[50],
            borderLeft: 'none',
            borderRight: 'none'
        },

        '& .fc .fc-col-header-cell-cushion': {
            display: 'block',
            padding: theme.spacing(1.25, 1),
            fontSize: theme.typography.caption.fontSize,
            fontWeight: 600,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            color: theme.palette.text.secondary
        },

        '& .fc .fc-daygrid-day-frame': {
            minHeight: 112
        },

        '& .fc .fc-daygrid-day, & .fc .fc-timegrid-col': {
            backgroundColor: theme.palette.background.paper
        },

        '& .fc .fc-day-sat, & .fc .fc-day-sun': {
            backgroundColor: weekendBackground
        },

        '& .fc .fc-daygrid-day.fc-day-today, & .fc .fc-timegrid-col.fc-day-today': {
            backgroundColor: theme.palette.background.paper,
            boxShadow: `inset 0 0 0 2px ${theme.palette.primary.main}`
        },

        [[
            '& .fc .fc-daygrid-day.fc-day-today.fc-day-sat',
            '& .fc .fc-daygrid-day.fc-day-today.fc-day-sun',
            '& .fc .fc-timegrid-col.fc-day-today.fc-day-sat',
            '& .fc .fc-timegrid-col.fc-day-today.fc-day-sun'
        ].join(', ')]: {
            backgroundColor: weekendBackground
        },

        '& .fc .fc-day-other': {
            backgroundColor: theme.palette.grey[50]
        },

        '& .fc .fc-day-other .fc-daygrid-day-top': {
            opacity: 0.45
        },

        '& .fc .fc-daygrid-day-top': {
            flexDirection: 'row',
            padding: theme.spacing(0.75, 0.75, 0)
        },

        '& .fc .fc-daygrid-day-number': {
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: 28,
            height: 28,
            padding: 0,
            borderRadius: '50%',
            fontWeight: 500,
            color: theme.palette.text.primary
        },

        '& .fc .fc-day-today .fc-daygrid-day-number': {
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.primary.contrastText,
            fontWeight: 700
        },

        '& .fc .fc-daygrid-event-harness': {
            margin: theme.spacing(0, 0.75, 0.5)
        },

        '& .fc .fc-daygrid-event': {
            margin: 0,
            padding: theme.spacing(0.5, 0.75)
        },

        '& .fc .fc-timegrid-event': {
            padding: theme.spacing(0.25, 0.5),
            boxShadow: `0 0 0 1px ${theme.palette.background.paper}`
        },

        '& .fc .fc-event': {
            cursor: 'pointer',
            borderRadius: theme.shape.borderRadius,
            border: 'none',
            borderLeftWidth: 3,
            borderLeftStyle: 'solid',
            borderLeftColor: theme.palette.secondary.dark,
            backgroundColor: theme.palette.secondary.main,
            color: theme.palette.secondary.contrastText,
            transition: theme.transitions.create('background-color'),
            '&:hover': {
                backgroundColor: theme.palette.secondary.dark
            }
        },

        '& .fc .fc-event.fc-event-past': {
            borderLeftColor: theme.palette.grey[400],
            backgroundColor: theme.palette.grey[200],
            color: theme.palette.text.secondary,
            '&:hover': {
                backgroundColor: theme.palette.grey[300]
            }
        },

        '& .fc .fc-event:has([data-simultaneous-badge]:hover)': {
            backgroundColor: theme.palette.secondary.main
        },

        '& .fc .fc-event.fc-event-past:has([data-simultaneous-badge]:hover)': {
            backgroundColor: theme.palette.grey[200]
        },

        '& .fc .fc-col-header-cell.fc-day-today': {
            backgroundColor: theme.palette.primary.main
        },

        '& .fc .fc-col-header-cell.fc-day-today .fc-col-header-cell-cushion': {
            color: theme.palette.primary.contrastText
        },

        '& .fc .fc-timegrid-slot-label-cushion': {
            fontSize: theme.typography.caption.fontSize,
            color: theme.palette.text.secondary
        },

        '& .fc .fc-event-main': {
            color: 'inherit'
        }
    };
});

export default CalendarSurface;
